import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  ChartBarIcon,
  Cog6ToothIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  ClipboardDocumentCheckIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  CursorArrowRaysIcon,
  FunnelIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const AnalyticsManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [settings, setSettings] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Form state
  const [formData, setFormData] = useState({
    // Google Analytics 4
    ga4_enabled: false,
    ga4_measurement_id: '',
    ga4_api_secret: '',
    ga4_stream_id: '',
    ga4_enhanced_ecommerce: true,
    ga4_debug_mode: false,

    // Facebook Pixel
    facebook_pixel_enabled: false,
    facebook_pixel_id: '',
    facebook_access_token: '',
    facebook_test_event_code: '',

    // Heatmaps
    heatmaps_enabled: false,
    hotjar_site_id: '',
    microsoft_clarity_id: '',

    // Custom Analytics
    custom_events_enabled: true,
    conversion_tracking_enabled: true,
    cohort_analysis_enabled: true,

    // Privacy & Compliance
    gdpr_compliance: true,
    cookie_consent_required: true,
    anonymize_ip: true,

    // Advanced Settings
    data_retention_days: 365,
    sampling_rate: 1.0,
    daily_reports_enabled: false,
    weekly_reports_enabled: true,

    // System
    is_active: true
  });

  const tabs = [
    { id: 'overview', name: 'סקירה כללית', icon: ChartBarIcon },
    { id: 'ga4', name: 'Google Analytics 4', icon: GlobeAltIcon },
    { id: 'facebook', name: 'Facebook Pixel', icon: DevicePhoneMobileIcon },
    { id: 'heatmaps', name: 'מפות חום', icon: CursorArrowRaysIcon },
    { id: 'advanced', name: 'הגדרות מתקדמות', icon: Cog6ToothIcon },
    { id: 'privacy', name: 'פרטיות ותאימות', icon: ShieldCheckIcon }
  ];

  useEffect(() => {
    fetchSettings();
    fetchDashboard();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/admin/analytics/settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const data = response.data.data;
        setSettings(data);
        setFormData({
          ...formData,
          ...data
        });
      }
    } catch (error) {
      toast.error('שגיאה בטעינת הגדרות האנליטיקה');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get('/api/admin/analytics/dashboard', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put('/api/admin/analytics/settings', formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setSettings(response.data.data);
        toast.success('הגדרות האנליטיקה נשמרו בהצלחה');
        fetchDashboard(); // Refresh dashboard data
      }
    } catch (error) {
      toast.error('שגיאה בשמירת הגדרות האנליטיקה');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (type) => {
    setTesting(prev => ({ ...prev, [type]: true }));
    try {
      const endpoint = type === 'ga4' ? '/api/admin/analytics/test-ga4' : '/api/admin/analytics/test-facebook-pixel';
      const response = await axios.post(endpoint, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `שגיאה בבדיקת ${type}`);
    } finally {
      setTesting(prev => ({ ...prev, [type]: false }));
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('he-IL').format(num);
  };

  const getStatusBadge = (enabled) => {
    return enabled ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircleIcon className="w-4 h-4 mr-1" />
        פעיל
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircleIcon className="w-4 h-4 mr-1" />
        לא פעיל
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ניהול אנליטיקה</h1>
          <p className="text-gray-600">
            ניהול וקביעת הגדרות מערכות האנליטיקה והמעקב באתר
          </p>
        </div>

        {/* Overview Cards */}
        {dashboardData && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <EyeIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="mr-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">צפיות בדף</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatNumber(dashboardData.stats.total_page_views)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CursorArrowRaysIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="mr-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">אירועים</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatNumber(dashboardData.stats.total_events)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="mr-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">המרות</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatNumber(dashboardData.stats.total_conversions)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FunnelIcon className="h-8 w-8 text-orange-600" />
                </div>
                <div className="mr-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">שיעור המרה</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardData.stats.conversion_rate}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Overview */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">סטטוס מערכות אנליטיקה</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Google Analytics 4</h3>
                  <p className="text-sm text-gray-500">מעקב מתקדם</p>
                </div>
                {getStatusBadge(dashboardData?.settings?.ga4_enabled)}
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Facebook Pixel</h3>
                  <p className="text-sm text-gray-500">מעקב המרות</p>
                </div>
                {getStatusBadge(dashboardData?.settings?.facebook_pixel_enabled)}
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">מפות חום</h3>
                  <p className="text-sm text-gray-500">ניתוח התנהגות</p>
                </div>
                {getStatusBadge(dashboardData?.settings?.heatmaps_enabled)}
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">אירועים מותאמים</h3>
                  <p className="text-sm text-gray-500">מעקב פנימי</p>
                </div>
                {getStatusBadge(dashboardData?.settings?.custom_events_enabled)}
              </div>
            </div>
          </div>
        </div>

        {/* Main Settings Panel */}
        <div className="bg-white rounded-lg shadow">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <Icon className="w-5 h-5 ml-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">סקירה כללית</h3>
                  <p className="text-gray-600 mb-6">
                    ניהול כללי של מערכות האנליטיקה והמעקב באתר. כאן תוכל להפעיל או להשבית את כל המערכות.
                  </p>
                </div>

                {/* Master Switch */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">מתג ראשי - אנליטיקה</h4>
                      <p className="text-sm text-gray-600">
                        הפעלה או השבתה של כל מערכות האנליטיקה באתר
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {/* Recent Activity */}
                {dashboardData?.recent_activity && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">פעילות אחרונה</h4>
                    <div className="space-y-3">
                      {dashboardData.recent_activity.map((activity, index) => (
                        <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                          <div className="flex-shrink-0">
                            {activity.type === 'page_view' && <EyeIcon className="w-5 h-5 text-blue-600" />}
                            {activity.type === 'purchase' && <CheckCircleIcon className="w-5 h-5 text-green-600" />}
                            {activity.type === 'add_to_cart' && <CursorArrowRaysIcon className="w-5 h-5 text-orange-600" />}
                          </div>
                          <div className="mr-3 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {activity.type === 'page_view' && `צפייה בדף ${activity.page}`}
                              {activity.type === 'purchase' && `רכישה בסך ${activity.value}₪`}
                              {activity.type === 'add_to_cart' && `הוספה לעגלה`}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(activity.timestamp).toLocaleString('he-IL')} • {activity.user_type}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'ga4' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Google Analytics 4</h3>
                    <p className="text-gray-600">הגדרות מעקב Google Analytics 4 מתקדם</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => testConnection('ga4')}
                      disabled={!formData.ga4_enabled || !formData.ga4_measurement_id || testing.ga4}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {testing.ga4 ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 ml-2"></div>
                      ) : (
                        <PlayIcon className="w-4 h-4 ml-2" />
                      )}
                      בדוק חיבור
                    </button>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.ga4_enabled}
                        onChange={(e) => handleInputChange('ga4_enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Measurement ID *
                    </label>
                    <input
                      type="text"
                      placeholder="G-XXXXXXXXXX"
                      value={formData.ga4_measurement_id}
                      onChange={(e) => handleInputChange('ga4_measurement_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      מזהה המדידה של GA4 (מתחיל ב-G-)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Secret
                    </label>
                    <input
                      type="password"
                      placeholder="API Secret למעקב server-side"
                      value={formData.ga4_api_secret}
                      onChange={(e) => handleInputChange('ga4_api_secret', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stream ID
                    </label>
                    <input
                      type="text"
                      placeholder="מזהה זרם הנתונים"
                      value={formData.ga4_stream_id}
                      onChange={(e) => handleInputChange('ga4_stream_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="ga4_enhanced_ecommerce"
                      checked={formData.ga4_enhanced_ecommerce}
                      onChange={(e) => handleInputChange('ga4_enhanced_ecommerce', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="ga4_enhanced_ecommerce" className="mr-2 block text-sm text-gray-900">
                      Enhanced E-commerce Tracking
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="ga4_debug_mode"
                      checked={formData.ga4_debug_mode}
                      onChange={(e) => handleInputChange('ga4_debug_mode', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="ga4_debug_mode" className="mr-2 block text-sm text-gray-900">
                      Debug Mode (לפיתוח בלבד)
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'facebook' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Facebook Pixel</h3>
                    <p className="text-gray-600">הגדרות מעקב Facebook Pixel ו-Conversions API</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => testConnection('facebook')}
                      disabled={!formData.facebook_pixel_enabled || !formData.facebook_pixel_id || testing.facebook}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {testing.facebook ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 ml-2"></div>
                      ) : (
                        <PlayIcon className="w-4 h-4 ml-2" />
                      )}
                      בדוק חיבור
                    </button>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.facebook_pixel_enabled}
                        onChange={(e) => handleInputChange('facebook_pixel_enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pixel ID *
                    </label>
                    <input
                      type="text"
                      placeholder="123456789012345"
                      value={formData.facebook_pixel_id}
                      onChange={(e) => handleInputChange('facebook_pixel_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Access Token (CAPI)
                    </label>
                    <input
                      type="password"
                      placeholder="Access Token לConversions API"
                      value={formData.facebook_access_token}
                      onChange={(e) => handleInputChange('facebook_access_token', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test Event Code
                    </label>
                    <input
                      type="text"
                      placeholder="קוד לבדיקת אירועים"
                      value={formData.facebook_test_event_code}
                      onChange={(e) => handleInputChange('facebook_test_event_code', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'heatmaps' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">מפות חום</h3>
                    <p className="text-gray-600">הגדרות Hotjar ו-Microsoft Clarity</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.heatmaps_enabled}
                      onChange={(e) => handleInputChange('heatmaps_enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hotjar Site ID
                    </label>
                    <input
                      type="text"
                      placeholder="1234567"
                      value={formData.hotjar_site_id}
                      onChange={(e) => handleInputChange('hotjar_site_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      מזהה האתר ב-Hotjar
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Microsoft Clarity ID
                    </label>
                    <input
                      type="text"
                      placeholder="abcdefghij"
                      value={formData.microsoft_clarity_id}
                      onChange={(e) => handleInputChange('microsoft_clarity_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      מזהה הפרויקט ב-Microsoft Clarity (חינמי!)
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="mr-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        המלצה: Microsoft Clarity
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>
                          Microsoft Clarity הוא כלי חינמי ללא הגבלות שמספק מפות חום, הקלטות משתמשים וניתוחים מתקדמים.
                          מומלץ להתחיל איתו לפני מעבר לפתרונות בתשלום כמו Hotjar.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">הגדרות מתקדמות</h3>
                  <p className="text-gray-600">הגדרות מתקדמות למערכות האנליטיקה</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      תקופת שמירת נתונים (ימים)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1095"
                      value={formData.data_retention_days}
                      onChange={(e) => handleInputChange('data_retention_days', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      שיעור דגימה (0.01-1.0)
                    </label>
                    <input
                      type="number"
                      min="0.01"
                      max="1.0"
                      step="0.01"
                      value={formData.sampling_rate}
                      onChange={(e) => handleInputChange('sampling_rate', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="custom_events_enabled"
                      checked={formData.custom_events_enabled}
                      onChange={(e) => handleInputChange('custom_events_enabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="custom_events_enabled" className="mr-2 block text-sm text-gray-900">
                      אירועים מותאמים אישית
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="conversion_tracking_enabled"
                      checked={formData.conversion_tracking_enabled}
                      onChange={(e) => handleInputChange('conversion_tracking_enabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="conversion_tracking_enabled" className="mr-2 block text-sm text-gray-900">
                      מעקב משפכי המרה
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="cohort_analysis_enabled"
                      checked={formData.cohort_analysis_enabled}
                      onChange={(e) => handleInputChange('cohort_analysis_enabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="cohort_analysis_enabled" className="mr-2 block text-sm text-gray-900">
                      ניתוח קבוצות לקוחות (Cohort Analysis)
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="daily_reports_enabled"
                      checked={formData.daily_reports_enabled}
                      onChange={(e) => handleInputChange('daily_reports_enabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="daily_reports_enabled" className="mr-2 block text-sm text-gray-900">
                      דוחות יומיים
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="weekly_reports_enabled"
                      checked={formData.weekly_reports_enabled}
                      onChange={(e) => handleInputChange('weekly_reports_enabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="weekly_reports_enabled" className="mr-2 block text-sm text-gray-900">
                      דוחות שבועיים
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">פרטיות ותאימות</h3>
                  <p className="text-gray-600">הגדרות פרטיות ותאימות לתקנות GDPR</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="gdpr_compliance"
                      checked={formData.gdpr_compliance}
                      onChange={(e) => handleInputChange('gdpr_compliance', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="gdpr_compliance" className="mr-2 block text-sm text-gray-900">
                      תאימות GDPR
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="cookie_consent_required"
                      checked={formData.cookie_consent_required}
                      onChange={(e) => handleInputChange('cookie_consent_required', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="cookie_consent_required" className="mr-2 block text-sm text-gray-900">
                      דרישת הסכמה לעוגיות
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="anonymize_ip"
                      checked={formData.anonymize_ip}
                      onChange={(e) => handleInputChange('anonymize_ip', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="anonymize_ip" className="mr-2 block text-sm text-gray-900">
                      אנונימיזציה של כתובות IP
                    </label>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="mr-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        חשוב לדעת
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          הגדרות הפרטיות משפיעות על איכות הנתונים שנאספים. 
                          מומלץ לאזן בין פרטיות המשתמשים לבין הצורך בנתונים לצורכי אנליטיקה.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
              ) : (
                <ClipboardDocumentCheckIcon className="w-4 h-4 ml-2" />
              )}
              {saving ? 'שומר...' : 'שמור הגדרות'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsManagement;
